import React, { useState } from 'react';
import { Table2, ChevronDown, ChevronRight, Upload, Hash, Type, Calendar, ToggleLeft, ArrowLeft } from 'lucide-react';

const TYPE_ICONS = {
  INTEGER: Hash, BIGINT: Hash, DOUBLE: Hash, FLOAT: Hash, HUGEINT: Hash,
  SMALLINT: Hash, TINYINT: Hash, UBIGINT: Hash, UINTEGER: Hash,
  VARCHAR: Type, TEXT: Type,
  DATE: Calendar, TIMESTAMP: Calendar, TIME: Calendar,
  BOOLEAN: ToggleLeft,
};

function TypeIcon({ type }) {
  const normalizedType = (type || '').toUpperCase().split('(')[0];
  const Icon = TYPE_ICONS[normalizedType] || Type;
  return <Icon size={10} style={{ flexShrink: 0, opacity: 0.4 }} />;
}

function TableEntry({ table, onTableClick }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div style={{ marginBottom: 2 }}>
      <button
        onClick={() => setExpanded(e => !e)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, width: '100%',
          background: 'none', border: 'none', color: 'var(--text)', cursor: 'pointer',
          padding: '5px 12px', borderRadius: 0, textAlign: 'left',
          fontFamily: 'var(--sans)', fontSize: 13,
          transition: 'background 0.1s',
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
        onMouseLeave={e => e.currentTarget.style.background = 'none'}
      >
        {expanded
          ? <ChevronDown size={11} style={{ color: 'var(--text3)', flexShrink: 0 }} />
          : <ChevronRight size={11} style={{ color: 'var(--text3)', flexShrink: 0 }} />}
        <Table2 size={12} style={{ color: 'var(--accent)', flexShrink: 0 }} />
        <span
          style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          onClick={e => { e.stopPropagation(); onTableClick(table.name); }}
        >{table.name}</span>
        <span style={{ color: 'var(--text3)', fontSize: 11, flexShrink: 0 }}>
          {table.rowCount?.toLocaleString()}
        </span>
      </button>
      {expanded && (
        <div style={{ paddingLeft: 28, paddingBottom: 4 }}>
          {table.schema.map(col => (
            <div key={col.name} style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '2px 12px 2px 0', color: 'var(--text2)', fontSize: 12,
            }}>
              <TypeIcon type={col.type} />
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{col.name}</span>
              <span style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{col.type}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Sidebar({ tables, onUploadClick, onTableClick, onHome }) {
  return (
    <div style={{
      width: 240, minWidth: 200, maxWidth: 280, height: '100%',
      background: 'var(--bg2)', borderRight: '1px solid var(--border)',
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '10px 12px', borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: 6, minHeight: 42,
      }}>
        {onHome && (
          <button
            onClick={onHome}
            title="Back to home"
            style={{
              background: 'none', border: 'none', color: 'var(--text2)',
              cursor: 'pointer', padding: '3px 4px', borderRadius: 3,
              display: 'flex', alignItems: 'center', transition: 'color 0.1s',
            }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--text)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--text2)'}
          >
            <ArrowLeft size={13} />
          </button>
        )}
        <span style={{ fontFamily: 'var(--sans)', fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>
          maca<span style={{ color: 'var(--orange)', fontWeight: 700 }}>row</span>ni
        </span>
        <span style={{ color: 'var(--border2)', fontSize: 11 }}>/</span>
        <span style={{ color: 'var(--text2)', fontSize: 11 }}>csv_query</span>
      </div>

      {/* Upload button */}
      <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>
        <button
          onClick={onUploadClick}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 7, padding: '7px 12px',
            background: 'var(--accent)', border: 'none', borderRadius: 4,
            color: '#fff', fontFamily: 'var(--sans)', fontSize: 12,
            fontWeight: 600, cursor: 'pointer', transition: 'background 0.15s',
            letterSpacing: '0.03em', textTransform: 'uppercase',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--accent2)'}
          onMouseLeave={e => e.currentTarget.style.background = 'var(--accent)'}
        >
          <Upload size={12} />
          Choose File
        </button>
      </div>

      {/* Tables list */}
      <div style={{ flex: 1, overflowY: 'auto', paddingTop: 6 }}>
        {tables.length === 0 ? (
          <div style={{
            padding: '20px 14px', textAlign: 'center',
            color: 'var(--text3)', fontSize: 12, lineHeight: 1.7,
          }}>
            No tables loaded.<br />Upload a CSV to begin.
          </div>
        ) : (
          tables.map(t => (
            <TableEntry key={t.name} table={t} onTableClick={onTableClick} />
          ))
        )}
      </div>

      {/* Footer */}
      <div style={{
        padding: '8px 12px', borderTop: '1px solid var(--border)',
        fontSize: 11, color: 'var(--text3)', display: 'flex', alignItems: 'center', gap: 5,
      }}>
        <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--green)' }} />
        DuckDB WASM ready
      </div>
    </div>
  );
}
